import { type FC, type HTMLAttributes, type ReactNode } from 'react'
import { Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import InfoMessageBottomCard, {
  type InfoMessageBottomCardProps,
} from '@DS/components/components/foundations/shapes/card/infoMessageBottomCard/InfoMessageBottomCard'
import MeaIcon, { type MeaIconProps } from '@DS/components/components/informationMessages/meaIcon/MeaIcon'
import ConditionalWrapper from '@DS/components/components/utils/wrapper/ConditionalWrapper'
import ExternalLink from '@DS/components/forms/links/externalLink/ExternalLink'
import LinkButton from '@DS/components/forms/links/linkButton/LinkButton'

import * as styles from './styles'

type WithoutBackground = {
  actionLabel?: never
  href?: never
  onClick?: never
  hasExternalLink?: never
  hasBackground?: false
} & HTMLAttributes<HTMLDivElement>

export type WithAction = {
  actionLabel: string
  href?: string
  onClick?: VoidFunction
  hasExternalLink?: boolean
  hasBackground: true
}

type WithoutAction = {
  actionLabel?: never
  href?: never
  onClick?: never
  hasExternalLink?: never
  hasBackground: true
}

type BackgroundVariantProps =
  | WithoutBackground
  | ((WithAction | WithoutAction) & Omit<InfoMessageBottomCardProps, 'severity' | 'ref' | 'color'>)

export type InfoMessageProps = {
  description: ReactNode
} & Pick<MeaIconProps, 'severity' | 'isAriaLabelDisabled'> &
  BackgroundVariantProps

const InfoMessage: FC<InfoMessageProps> = ({
  severity,
  description,
  isAriaLabelDisabled = false,
  hasBackground = false,
  actionLabel,
  href,
  onClick,
  hasExternalLink = false,
  ...rest
}) => {
  const theme = useTheme()

  const action = actionLabel && (
    <ConditionalWrapper
      condition={hasExternalLink}
      wrapper={(children) => <ExternalLink href={href}>{children}</ExternalLink>}
      defaultWrapper={(children) => (
        <LinkButton {...(href && { isNextRoute: true, href, component: 'a' })} onClick={onClick}>
          {children}
        </LinkButton>
      )}
    >
      {actionLabel}
    </ConditionalWrapper>
  )

  return (
    <ConditionalWrapper
      condition={hasBackground}
      wrapper={(children) => (
        <InfoMessageBottomCard {...rest} severity={severity}>
          <div css={styles.container} data-test="mea-mini">
            {children}
          </div>

          {action && <div css={styles.actionWrapper(theme)}>{action}</div>}
        </InfoMessageBottomCard>
      )}
      defaultWrapper={(children) => (
        <div {...rest} css={styles.container} data-test="mea-mini">
          {children}
        </div>
      )}
    >
      <MeaIcon css={styles.icon(theme)} severity={severity} isAriaLabelDisabled={isAriaLabelDisabled} />

      <Typography variant="subtitle1" css={styles.description(severity, theme)}>
        {description}
      </Typography>
    </ConditionalWrapper>
  )
}

export default InfoMessage
